import DOMPurify from 'dompurify';

// 单位换算
// - mm ↔ px：依据 96dpi 与 25.4mm/in 换算
// - pt ↔ mm：依据 72pt/in 与 25.4mm/in 换算
export const mmToPx = (mm) => mm * 96 / 25.4;
export const pxToMm = (px) => px * 25.4 / 96;
export const ptToMm = (pt) => pt * 25.4 / 72;

/**
 * 从 @page 读取纸张与边距设置
 * - 兼容 Aspose/Word 导出的 @page size/margin，单位为 pt
 * - 返回 mm 单位并四舍五入到 0.1 精度
 */
export const extractPageSettingsFromHtml = (html) => {
  const s = String(html || '');
  const sizeM = s.match(/@page\s+[^\{]*\{[^\}]*size\s*:\s*([0-9.]+)pt\s+([0-9.]+)pt/i);
  const marginM = s.match(/@page\s+[^\{]*\{[^\}]*margin\s*:\s*([0-9.]+)pt\s+([0-9.]+)pt\s+([0-9.]+)pt/i);
  if (!sizeM && !marginM) return null;
  const widthMm = sizeM ? ptToMm(parseFloat(sizeM[1] || '595.3')) : 210;
  const heightMm = sizeM ? ptToMm(parseFloat(sizeM[2] || '841.9')) : 297;
  let mt = 25, mr = 25, mb = 25, ml = 25;
  if (marginM) {
    mt = ptToMm(parseFloat(marginM[1] || '0'));
    mr = ptToMm(parseFloat(marginM[2] || '0'));
    mb = ptToMm(parseFloat(marginM[3] || '0'));
    ml = mr;
  }
  return {
    widthMm: Math.round(widthMm * 10) / 10,
    heightMm: Math.round(heightMm * 10) / 10,
    marginTopMm: Math.round(mt * 10) / 10,
    marginRightMm: Math.round(mr * 10) / 10,
    marginBottomMm: Math.round(mb * 10) / 10,
    marginLeftMm: Math.round(ml * 10) / 10,
  };
};

/**
 * 将 HTML 拆分为页眉/正文/页脚
 * 识别策略：
 * - 按 data-role/data-part/data-header/data-footer 或 Aspose 样式标记
 * - 若无显式标记，按 <hr> 数量或首个标题位置推断
 * - 最终保证返回 body 不包含 <style>，便于分页渲染
 */
export const splitHeaderBodyFooter = (s) => {
  const src = String(s || '');
  const wrap = document.createElement('div');
  wrap.innerHTML = src;
  const styleEls = wrap.querySelectorAll('style');
  styleEls.forEach(el => el.parentNode && el.parentNode.removeChild(el));
  const hdrEl = wrap.querySelector('[data-role="header"],[data-part="header"],[data-header="true"],[style*="-aw-headerfooter-type:header"],.doc-header,.aspose-header,.section-header,header');
  const ftrEl = wrap.querySelector('[data-role="footer"],[data-part="footer"],[data-footer="true"],[style*="-aw-headerfooter-type:footer"],.doc-footer,.aspose-footer,.section-footer,footer');
  if (hdrEl || ftrEl) {
    // 显式 header/footer 元素：优先按标记拆分，避免误把页眉页脚参与正文分页
    const header = hdrEl ? hdrEl.outerHTML : '';
    if (hdrEl) hdrEl.remove();
    const footer = ftrEl ? ftrEl.outerHTML : '';
    if (ftrEl) ftrEl.remove();
    let raw = wrap.innerHTML;
    const temp = document.createElement('div');
    temp.innerHTML = raw;
    const htmlEl = temp.querySelector('html');
    let content = htmlEl ? (htmlEl.querySelector('body') ? htmlEl.querySelector('body').innerHTML : htmlEl.innerHTML) : raw;
    const temp2 = document.createElement('div');
    temp2.innerHTML = content;
    const sec = temp2.querySelector('div.Section1');
    if (sec) content = sec.innerHTML;
    const body = String(content || '').replace(/<style[\s\S]*?<\/style>/gi, '');
    return { header, body, footer };
  }
  const hrs = [...src.matchAll(/<hr[\s\S]*?>/gi)];
  if (hrs.length >= 2) {
    const first = hrs[0].index || 0;
    const last = hrs[hrs.length - 1].index || 0;
    const firstEnd = first + (hrs[0][0] || '').length;
    const lastEnd = last + (hrs[hrs.length - 1][0] || '').length;
    let body = src.slice(firstEnd, last);
    body = String(body || '').replace(/<style[\s\S]*?<\/style>/gi, '');
    return { header: src.slice(0, first), body, footer: src.slice(lastEnd) };
  }
  if (hrs.length === 1) {
    const idx = hrs[0].index || 0;
    const end = idx + (hrs[0][0] || '').length;
    let body = src.slice(end);
    body = String(body || '').replace(/<style[\s\S]*?<\/style>/gi, '');
    return { header: src.slice(0, idx), body, footer: '' };
  }
  const h = src.match(/<h[1-6][^>]*>[\s\S]*?<\/h[1-6]>/i);
  if (h && h.index != null) {
    let body = src.slice(h.index);
    body = String(body || '').replace(/<style[\s\S]*?<\/style>/gi, '');
    return { header: src.slice(0, h.index), body, footer: '' };
  }
  const raw = src;
  const temp = document.createElement('div');
  temp.innerHTML = raw;
  const htmlEl2 = temp.querySelector('html');
  let content = htmlEl2 ? (htmlEl2.querySelector('body') ? htmlEl2.querySelector('body').innerHTML : htmlEl2.innerHTML) : raw;
  const temp2 = document.createElement('div');
  temp2.innerHTML = content;
  const sec = temp2.querySelector('div.Section1');
  if (sec) content = sec.innerHTML;
  const body = String(content || '').replace(/<style[\s\S]*?<\/style>/gi, '');
  return { header: '', body, footer: '' };
};

export const composeHtml = (h, b, f) => `${h || ''}${b || ''}${f || ''}`;

const escapeAttr = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;');

const buildOpenTagFromEl = (el) => {
  const tag = (el?.tagName || '').toLowerCase();
  if (!tag) return '';
  const names = typeof el.getAttributeNames === 'function' ? el.getAttributeNames() : [];
  const attrs = names
    .map((n) => {
      const val = el.getAttribute(n);
      if (val == null) return '';
      return ` ${n}="${escapeAttr(val)}"`;
    })
    .join('');
  return `<${tag}${attrs}>`;
};

export const extractOuterShell = (segmentHtml) => {
  const raw = String(segmentHtml || '').trim();
  if (!raw) return { shell: null, innerHtml: '' };
  const wrap = document.createElement('div');
  wrap.innerHTML = raw;
  const el = wrap.firstElementChild;
  if (!el || wrap.childElementCount !== 1) return { shell: null, innerHtml: raw };
  const tag = (el.tagName || '').toLowerCase();
  const style = (el.getAttribute('style') || '').toLowerCase();
  const role = (el.getAttribute('data-role') || '').toLowerCase();
  const part = (el.getAttribute('data-part') || '').toLowerCase();
  const dataHeader = (el.getAttribute('data-header') || '').toLowerCase();
  const dataFooter = (el.getAttribute('data-footer') || '').toLowerCase();
  const keep = tag === 'div' || tag === 'header' || tag === 'footer'
    || style.includes('-aw-headerfooter-type')
    || role === 'header' || role === 'footer'
    || part === 'header' || part === 'footer'
    || dataHeader === 'true' || dataFooter === 'true';
  if (!keep) return { shell: null, innerHtml: raw };
  const openTag = buildOpenTagFromEl(el);
  const closeTag = `</${tag}>`;
  return { shell: { tag, openTag, closeTag }, innerHtml: el.innerHTML || '' };
};

export const unwrapOuterShell = (segmentHtml, shell) => {
  if (!shell) return String(segmentHtml || '');
  const raw = String(segmentHtml || '').trim();
  if (!raw) return '';
  const wrap = document.createElement('div');
  wrap.innerHTML = raw;
  const el = wrap.firstElementChild;
  if (!el || wrap.childElementCount !== 1) return String(segmentHtml || '');
  const tag = (el.tagName || '').toLowerCase();
  if (tag !== shell.tag) return String(segmentHtml || '');
  return el.innerHTML || '';
};

export const wrapOuterShell = (innerHtml, shell) => {
  const inner = String(innerHtml || '');
  if (!shell) return inner;
  return `${shell.openTag}${inner}${shell.closeTag}`;
};

/**
 * 规范化 Word/Aspose 列表为语义化 <ol>/<ul>
 * - 将带有 mso-list/-aw-import 标记的 <p> 转为 <li>
 * - 删除前导编号/符号 span 与无关样式，保持 margin/padding/list-style
 * - 输出 HTML 便于后续编辑与分页
 */
export const normalizeAsposeLists = (html) => {
  const wrap = document.createElement('div');
  wrap.innerHTML = String(html || '');
  const isListP = (p) => {
    const st = (p.getAttribute('style') || '').toLowerCase();
    return st.includes('-aw-import:list-item') || st.includes('mso-list') || st.includes('-aw-list-level-number');
  };
  const listTypeOf = (p) => {
    const st = (p.getAttribute('style') || '').toLowerCase();
    if (st.includes('-aw-list-number-format') || st.includes('decimal') || st.includes('lower-alpha') || st.includes('upper-alpha') || st.includes('lower-roman') || st.includes('upper-roman')) return 'ol';
    return 'ol';
  };
  const cleanLeadingMarker = (html) => {
    let s = String(html || '');
    s = s.replace(/^\s*<span[^>]*>\s*[0-9a-z]+[\.\)\:]?\s*<\/span>\s*/i, '');
    s = s.replace(/^\s*[•◦●]\s*/i, '');
    return s;
  };
  const ps = Array.from(wrap.querySelectorAll('p'));
  for (let i = 0; i < ps.length; i++) {
    const p = ps[i];
    if (!isListP(p)) continue;
    const type = listTypeOf(p);
    const parent = p.parentNode;
    const prev = p.previousElementSibling;
    let listEl = prev && prev.tagName && prev.tagName.toLowerCase() === type && prev.getAttribute('data-gen-list') === '1' ? prev : null;
    if (!listEl) {
      listEl = document.createElement(type);
      listEl.setAttribute('data-gen-list', '1');
      parent.insertBefore(listEl, p);
    }
    const li = document.createElement('li');
    li.innerHTML = cleanLeadingMarker(p.innerHTML || '');
    listEl.appendChild(li);
    parent.removeChild(p);
  }
  const blockTags = new Set(['p', 'ul', 'ol', 'table', 'div', 'blockquote', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre']);
  const shouldDropLeadingIgnoreSpan = (el) => {
    if (!el || el.nodeType !== 1) return false;
    if ((el.tagName || '').toLowerCase() !== 'span') return false;
    const st = String(el.getAttribute('style') || '').toLowerCase();
    if (!st.includes('-aw-import:ignore')) return false;
    const t = String(el.textContent || '').replace(/\u00a0/g, '').trim();
    return !t;
  };
  Array.from(wrap.querySelectorAll('li')).forEach((li) => {
    while (li.firstElementChild && shouldDropLeadingIgnoreSpan(li.firstElementChild)) {
      li.removeChild(li.firstElementChild);
    }

    const directP = li.querySelector(':scope > p');
    if (directP) return;

    const p = document.createElement('p');
    p.setAttribute('style', 'margin:0;');
    const nodes = Array.from(li.childNodes);
    let firstBlockEl = null;
    for (const n of nodes) {
      if (n.nodeType === 1 && blockTags.has((n.tagName || '').toLowerCase())) {
        firstBlockEl = n;
        break;
      }
      p.appendChild(n);
    }
    if (!p.childNodes.length) {
      p.appendChild(document.createElement('br'));
    }
    if (firstBlockEl) li.insertBefore(p, firstBlockEl);
    else li.appendChild(p);
  });
  Array.from(wrap.querySelectorAll('li > p')).forEach(p => {
    const st = p.getAttribute('style') || '';
    const cleaned = st
      .split(';')
      .map(s => s.trim())
      .filter(s => s && !/^(text-indent|margin-left|padding-left|\-aw\-import|\-aw\-list\-level\-number|\-aw\-list\-number\-format)/i.test(s))
      .join(';');
    p.setAttribute('style', (cleaned ? cleaned + ';' : '') + 'margin:0;');
  });
  Array.from(wrap.querySelectorAll('ol')).forEach(el => {
    const st = el.getAttribute('style') || '';
    const base = 'list-style-type:decimal;list-style-position:outside;padding-left:18px;';
    el.setAttribute('style', (st ? st + ';' : '') + base);
  });
  Array.from(wrap.querySelectorAll('ul')).forEach(el => {
    const st = el.getAttribute('style') || '';
    const base = 'list-style-type:disc;list-style-position:outside;padding-left:18px;';
    el.setAttribute('style', (st ? st + ';' : '') + base);
  });
  return wrap.innerHTML;
};

/**
 * 规范化字体与字号为显式内联样式
 * - 通过计算样式（window.getComputedStyle）获取继承的字体/字号
 * - 将纯文本节点包裹为 <span style="font-family:...;font-size:...">
 * - 将 pt 转为 px，避免打印/导出差异
 */
export const normalizeInlineFonts = (html) => {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = String(html || '');
  const temp = document.createElement('div');
  temp.style.position = 'absolute';
  temp.style.visibility = 'hidden';
  temp.style.left = '-99999px';
  temp.style.top = '0';
  document.body.appendChild(temp);
  temp.appendChild(wrapper);
  const apply = (node, inhFf, inhFs) => {
    if (!node) return;
    if (node.nodeType === 1) {
      const style = node.getAttribute('style') || '';
      const fsM = style.match(/font-size:\s*([^;]+)/i);
      const ffM = style.match(/font-family:\s*([^;]+)/i);
      let fs = fsM ? fsM[1].trim() : '';
      let ff = ffM ? ffM[1].trim() : '';
      if (!fs || !ff) {
        const cs = window.getComputedStyle(node);
        if (!fs) fs = cs.fontSize || '';
        if (!ff) ff = cs.fontFamily || '';
      }
      if (!fs) fs = inhFs || '';
      if (!ff) ff = inhFf || '';
      if (fs && /pt$/i.test(fs)) {
        const v = parseFloat(fs);
        if (!isNaN(v)) fs = Math.round(v * 96 / 72) + 'px';
      }
      Array.from(node.childNodes).forEach(ch => apply(ch, ff, fs));
    } else if (node.nodeType === 3) {
      const txt = String(node.textContent || '');
      if (!txt.trim()) return;
      const span = document.createElement('span');
      const styles = [];
      if (inhFs) styles.push(`font-size:${inhFs}`);
      if (inhFf) styles.push(`font-family:${inhFf}`);
      if (styles.length) span.setAttribute('style', styles.join(';'));
      span.textContent = txt;
      node.parentNode.replaceChild(span, node);
    }
  };
  Array.from(wrapper.childNodes).forEach(ch => apply(ch, null, null));
  const htmlOut = wrapper.innerHTML;
  document.body.removeChild(temp);
  return htmlOut;
};

export const escapeHtml = (s) => String(s || '')
  .replace(/&/g,'&amp;')
  .replace(/</g,'&lt;')
  .replace(/>/g,'&gt;')
  .replace(/"/g,'&quot;')
  .replace(/'/g,'&#39;');

const formatDateToZh = (val) => {
  const s = String(val || '').trim();
  if (!s) return '';
  if (s.includes('年') && s.includes('月') && s.includes('日')) return s;
  const datePart = s.split(' ')[0];
  const m = datePart.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (!m) return s;
  const y = String(m[1] || '');
  const mo = String(m[2] || '').padStart(2, '0');
  const d = String(m[3] || '').padStart(2, '0');
  return `${y}年${mo}月${d}日`;
};

/**
 * 提取占位符键名数组
 * - 支持 {{...}} 包裹的内容；内部可能包含标签，先去标签再归一化空白
 */
export const extractPlaceholders = (text) => {
  const set = new Set();
  const html = String(text || '');
  const re = /\{\{[\s\S]*?\}\}/g;
  let m;
  while ((m = re.exec(html)) !== null) {
    const inner = m[0].slice(2, -2);
    const k = inner.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    if (k) set.add(k);
  }
  return Array.from(set);
};

export const highlightAndFill = (html, values) => {
  const src = String(html || '');
  const re = /\{\{[\s\S]*?\}\}/g;
  const filledStyle = 'background:rgba(255,245,102,0.75);white-space:nowrap;padding:0 2px;border-radius:2px;';
  const emptyStyle = 'background:rgba(255,245,102,0.75);white-space:nowrap;padding:0 2px;border-radius:2px;text-decoration-line:underline;text-decoration-style:dashed;text-decoration-color:rgba(230,172,0,0.95);text-underline-offset:2px;';
  return src.replace(re, (m) => {
    const inner = m.slice(2, -2);
    const key = inner.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    const rawVal = (values || {})[key] || '';
    const val = formatDateToZh(rawVal);
    return val
      ? `<span style="${filledStyle}">${escapeHtml(val)}</span>`
      : `<span style="${emptyStyle}">{{${key}}}</span>`;
  });
};

/**
 * 测量页眉/页脚在指定宽度下的高度
 * - 以隐藏容器承载，计算 scrollHeight 作为近似高度
 * - 多次测量时清空容器，避免累积高度误差
 */
export const measureHeaderFooterHeight = (header, footer, widthPx) => {
  const host = document.createElement('div');
  host.style.position = 'absolute';
  host.style.visibility = 'hidden';
  host.style.left = '-99999px';
  host.style.top = '0';
  host.style.width = widthPx + 'px';
  document.body.appendChild(host);
  let hH = 0, fH = 0;
  if (header && String(header).trim()) {
    const h = document.createElement('div');
    h.innerHTML = String(header || '');
    host.appendChild(h);
    hH = host.scrollHeight;
    host.innerHTML = '';
  }
  if (footer && String(footer).trim()) {
    const f = document.createElement('div');
    f.innerHTML = String(footer || '');
    host.appendChild(f);
    fH = host.scrollHeight;
    host.innerHTML = '';
  }
  document.body.removeChild(host);
  return { headerH: hH, footerH: fH };
};

/**
 * 根据显式分页标记切分内容段
 * 支持标记：
 * - <hr>、page-break-before/after: always
 * - Aspose/Word 的 -aw-* 标记或锚点 a[name/id*="page-break"]
 */
export const splitByExplicitBreaks = (html) => {
  const wrap = document.createElement('div');
  wrap.innerHTML = String(html || '');
  const segments = [];
  let current = document.createElement('div');
  const blocks = Array.from(wrap.childNodes);
  for (let i = 0; i < blocks.length; i++) {
    const node = blocks[i];
    const tag = (node.tagName || '').toLowerCase();
    const style = (node.getAttribute && (node.getAttribute('style') || '').toLowerCase()) || '';
    const isAnchorBreak = tag === 'a' && ((node.getAttribute('name') || '').toLowerCase().indexOf('page-break') >= 0);
    const hasAnchorInside = !isAnchorBreak && node.querySelector && (node.querySelector('a[name*="page-break"],a[id*="page-break"]'));
    const isBreak = tag === 'hr' || style.includes('page-break') || style.includes('-aw-page-break') || style.includes('-aw-section-break') || isAnchorBreak;
    const before = style.includes('page-break-before') && style.includes('always');
    const after = style.includes('page-break-after') && style.includes('always');
    if (hasAnchorInside) {
      const cloned = node.cloneNode(true);
      Array.from(cloned.querySelectorAll('a[name*="page-break"],a[id*="page-break"]'))
        .forEach(a => { if (a.parentNode) a.parentNode.removeChild(a); });
      current.appendChild(cloned);
      if (current.childNodes.length) segments.push(current.innerHTML);
      current = document.createElement('div');
      continue;
    }
    if (isBreak || before) {
      if (current.childNodes.length) segments.push(current.innerHTML);
      current = document.createElement('div');
      if (isBreak) continue;
    }
    current.appendChild(node.cloneNode(true));
    if (after) {
      segments.push(current.innerHTML);
      current = document.createElement('div');
    }
  }
  if (current.childNodes.length) segments.push(current.innerHTML);
  return segments.length ? segments : [wrap.innerHTML];
};

/**
 * 逐段分页
 * - 以隐藏容器模拟逐块布局，溢出即换页
 * - 列表按 <li> 逐项拆分、表格按行拆分并保留 thead
 * - div/p 作为容器递归拆分，尽量保留样式与类名
 */
export const paginateSegment = (html, widthPx, maxContentH) => {
  const out = [];
  const host = document.createElement('div');
  host.style.position = 'absolute';
  host.style.visibility = 'hidden';
  host.style.left = '-99999px';
  host.style.top = '0';
  host.style.width = widthPx + 'px';
  document.body.appendChild(host);
  const wrapper = document.createElement('div');
  wrapper.innerHTML = String(html || '');
  const nodes = Array.from(wrapper.childNodes);
  let page = document.createElement('div');
  host.innerHTML = '';
  host.appendChild(page);
  // 以 scrollHeight 作为近似布局高度：适配富文本在浏览器中的实际排版结果
  const fits = () => host.scrollHeight <= maxContentH;
  const pushPage = () => { if (page.childNodes.length) out.push(page.innerHTML); page = document.createElement('div'); host.innerHTML = ''; host.appendChild(page); };
  const tryAppend = (node) => { page.appendChild(node); if (fits()) return true; page.removeChild(page.lastChild); return false; };
  const cloneNode = (n) => n.cloneNode(true);
  const appendOrSplit = (n) => {
    if (tryAppend(cloneNode(n))) return;
    const tag = (n.tagName || '').toLowerCase();
    if (tag === 'ul' || tag === 'ol') {
      // 列表：按 <li> 逐项添加，溢出则把当前 <li> 放到下一页的同类型列表中
      const list = document.createElement(tag);
      page.appendChild(list);
      const items = Array.from(n.children || []);
      for (let i = 0; i < items.length; i++) {
        const li = items[i].cloneNode(true);
        list.appendChild(li);
        if (!fits()) {
          list.removeChild(list.lastChild);
          pushPage();
          const newList = document.createElement(tag);
          page.appendChild(newList);
          newList.appendChild(li);
        }
      }
      return;
    }
    if (tag === 'table') {
      // 表格：按行拆分并尽量保留 thead，避免跨页后表头丢失
      const tbl = document.createElement('table');
      const thead = n.querySelector('thead');
      const tbody = n.querySelector('tbody') || n;
      if (thead) tbl.appendChild(cloneNode(thead));
      page.appendChild(tbl);
      const rows = Array.from(tbody.querySelectorAll('tr'));
      for (let i = 0; i < rows.length; i++) {
        const tr = rows[i].cloneNode(true);
        if (!tbl.querySelector('tbody')) tbl.appendChild(document.createElement('tbody'));
        tbl.querySelector('tbody').appendChild(tr);
        if (!fits()) {
          tbl.querySelector('tbody').removeChild(tbl.querySelector('tbody').lastChild);
          pushPage();
          const nextTbl = document.createElement('table');
          if (thead) nextTbl.appendChild(cloneNode(thead));
          const nextTbody = document.createElement('tbody');
          nextTbl.appendChild(nextTbody);
          page.appendChild(nextTbl);
          nextTbody.appendChild(tr);
        }
      }
      return;
    }
    if (tag === 'div') {
      // 容器块：递归拆分子节点，尽量保持原容器的 style/class
      const container = document.createElement('div');
      const st = n.getAttribute && n.getAttribute('style');
      const cls = n.getAttribute && n.getAttribute('class');
      if (st) container.setAttribute('style', st);
      if (cls) container.setAttribute('class', cls);
      page.appendChild(container);
      const children = Array.from(n.childNodes);
      for (let i = 0; i < children.length; i++) {
        const child = children[i].cloneNode(true);
        container.appendChild(child);
        if (!fits()) {
          container.removeChild(container.lastChild);
          pushPage();
          const nextContainer = document.createElement('div');
          if (st) nextContainer.setAttribute('style', st);
          if (cls) nextContainer.setAttribute('class', cls);
          page.appendChild(nextContainer);
          nextContainer.appendChild(child);
        }
      }
      return;
    }
    if (tag === 'p') {
      // 段落：拆分子节点（文本/行内标签），让长段落也能跨页
      const container = document.createElement('p');
      const st = n.getAttribute && n.getAttribute('style');
      const cls = n.getAttribute && n.getAttribute('class');
      if (st) container.setAttribute('style', st);
      if (cls) container.setAttribute('class', cls);
      page.appendChild(container);
      const children = Array.from(n.childNodes);
      for (let i = 0; i < children.length; i++) {
        const child = children[i].cloneNode(true);
        container.appendChild(child);
        if (!fits()) {
          container.removeChild(container.lastChild);
          pushPage();
          const nextContainer = document.createElement('p');
          if (st) nextContainer.setAttribute('style', st);
          if (cls) nextContainer.setAttribute('class', cls);
          page.appendChild(nextContainer);
          nextContainer.appendChild(child);
        }
      }
      return;
    }
    // 其他块级节点：不做细粒度拆分，按整体换页
    if (page.childNodes.length === 0) {
      page.appendChild(cloneNode(n));
    } else {
      pushPage();
      page.appendChild(cloneNode(n));
    }
  };
  for (let i = 0; i < nodes.length; i++) appendOrSplit(nodes[i]);
  if (page.childNodes.length) out.push(page.innerHTML);
  document.body.removeChild(host);
  return out;
};

/**
 * 全文分页
 * - 计算内容宽度与页眉/页脚高度，得到可用内容高度
 * - 按显式段落切分后对每段执行 paginateSegment
 */
export const paginateEntire = (html, header, footer, settings) => {
  const widthPx = mmToPx(settings.widthMm - settings.marginLeftMm - settings.marginRightMm);
  const maxContentH = mmToPx(settings.heightMm - settings.marginTopMm - settings.marginBottomMm);
  const segments = splitByExplicitBreaks(html);
  const pages = [];
  for (let i = 0; i < segments.length; i++) {
    const partPages = paginateSegment(segments[i], widthPx, maxContentH);
    for (let j = 0; j < partPages.length; j++) pages.push(partPages[j]);
  }
  return pages;
};

/**
 * 预览渲染用的 HTML 白名单消毒（前端）
 * - 目标：降低 dangerouslySetInnerHTML 直接渲染带来的 XSS 风险
 * - 策略：先用 DOMPurify 做基础净化，再补充移除事件属性、危险 URL scheme 与可疑 style
 * - 说明：这是“显示层”净化，服务端仍需在落库前净化，形成双保险
 */
export const sanitizeHtmlForPreview = (html) => {
  const dirty = String(html || '');
  if (!dirty.trim()) return '';
  // 1) DOMPurify：基于浏览器 DOM 的净化，能处理常见的标签/属性注入
  const purified = DOMPurify.sanitize(dirty, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'link', 'meta', 'base', 'style', 'form', 'input', 'button', 'textarea'],
  });
  const wrap = document.createElement('div');
  wrap.innerHTML = String(purified || '');
  const nodes = wrap.querySelectorAll('*');
  nodes.forEach((el) => {
    // 2) 事件属性（onload/onclick/...）：直接移除，避免绕过
    const attrs = typeof el.getAttributeNames === 'function' ? el.getAttributeNames() : [];
    attrs.forEach((name) => {
      const n = String(name || '').toLowerCase();
      if (n.startsWith('on')) el.removeAttribute(name);
    });
    const cleanUrlAttr = (attr) => {
      const raw = el.getAttribute(attr);
      if (!raw) return;
      const v = String(raw).trim().toLowerCase();
      // 3) URL scheme 过滤：javascript:/vbscript:/data: 属于高危输入，直接清掉
      if (v.startsWith('javascript:') || v.startsWith('vbscript:') || v.startsWith('data:')) {
        el.removeAttribute(attr);
      }
    };
    cleanUrlAttr('href');
    cleanUrlAttr('src');
    cleanUrlAttr('xlink:href');
    const style = el.getAttribute('style');
    if (style) {
      const s = String(style).toLowerCase();
      // 4) style 内的表达式/协议注入：移除整段 style，避免 CSS 表达式/URL 绕过
      if (s.includes('expression(') || s.includes('javascript:') || s.includes('vbscript:') || s.includes('data:')) {
        el.removeAttribute('style');
      }
    }
  });
  return wrap.innerHTML;
};
